import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { shellHost } from '@/components/shellHost';

/**
 * A short notice floating at the foot of the screen, above the transport —
 * "Added to Friday Gig", "saved on this device". It used to print INSIDE the
 * workspace header's action row, which wrapped the header a line taller and
 * put the message wherever that row happened to fall (user, Sep 2026: "the
 * toast appeared randomly… maybe at the bottom"). A portal to the body keeps
 * it out of every layout (into the app shell, so it wears the style's ink, not
 * the root palette's teal: shellHost); `--transport-height` (published on :root by the
 * transport bar) lifts it clear of the band's controls, and it takes no
 * pointer events so it never covers a tap — except its actions (an Undo; an
 * Open and an Undo on the playlist notice), where it has them.
 */
export interface ToastAction {
  label: string;
  onClick: () => void;
  testId?: string;
}

export function Toast({
  children,
  role = 'status',
  testId,
  action,
  actions,
}: {
  children: ReactNode;
  /** The inner content may carry its own live region; pass null to leave it to that. */
  role?: 'status' | null;
  testId?: string;
  action?: ToastAction;
  /** Several, in order; `action` is the one-action shorthand. */
  actions?: ToastAction[];
}) {
  const all = actions ?? (action ? [action] : []);
  return createPortal(
    <div className="app-toast" role={role ?? undefined} data-testid={testId}>
      {children}
      {all.map((item) => (
        <button
          key={item.label}
          type="button"
          className="app-toast-action"
          onClick={item.onClick}
          data-testid={item.testId}
        >
          {item.label}
        </button>
      ))}
    </div>,
    shellHost(),
  );
}
